import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard, Building2, Users, FileSpreadsheet, Database, Bell,
  FileText, Settings, LogOut, Gauge, Menu, X, UserPlus, ClipboardCheck,
  IndianRupee, ScrollText, Calculator, Settings2, BarChart3, Plug,
  Sparkles, ClipboardList, Inbox, ChevronDown, MessageCircle, Fuel,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COLLAPSE_KEY = 'voltide.sidebar.collapsed';

// Source of truth for sidebar IA. Top-level items live outside any section.
const NAV_CONFIG = (ALL) => ({
  top: [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ALL, badge: 'alarms' },
  ],
  sections: [
    {
      id: 'operations',
      label: 'Operations',
      items: [
        { name: 'Sites',     path: '/sites',     icon: Building2,     roles: ALL },
        { name: 'Clients',   path: '/clients',   icon: Users,         roles: ALL },
        { name: 'JEM Portal', path: '/integrations/jem', icon: Plug,  roles: ALL },
        { name: 'WhatsApp',   path: '/integrations/whatsapp', icon: MessageCircle, roles: ALL },
        { name: 'Metering',  path: '/metering',  icon: Gauge,         roles: ALL },
        { name: 'Billing',   path: '/billing',   icon: FileSpreadsheet, roles: ALL },
      ],
    },
    {
      id: 'sales',
      label: 'Sales',
      items: [
        { name: 'Sales Inbox', path: '/sales/inbox',          aliasFor: '/sales-inbox',         icon: Inbox,          roles: ALL, badge: 'inbox' },
        { name: 'Prospects',   path: '/sales/prospects',      aliasFor: '/prospects',           icon: UserPlus,       roles: ALL },
        { name: 'Verify Queue', path: '/sales/verify-queue',  aliasFor: '/verification-queue',  icon: ClipboardCheck, roles: ALL, badge: 'verify' },
        { name: 'Proposals',   path: '/sales/proposals',      aliasFor: '/proposals',           icon: ScrollText,     roles: ALL },
        { name: 'Pricing & Catalog', path: '/sales/pricing-catalog', aliasFor: '/pricing',     icon: IndianRupee,    roles: ALL },
        { name: 'Sales Agent', path: '/sales-agent',          icon: Sparkles,       roles: ALL },
      ],
    },
    {
      id: 'finance',
      label: 'Finance Tools',
      items: [
        { name: 'Rental Sandbox',  path: '/rental-sandbox',       icon: Calculator,    roles: ALL },
        { name: 'Config Sandbox',  path: '/configurator-sandbox', icon: Settings2,     roles: ALL },
        { name: 'Config Summary',  path: '/config-summary',       icon: ClipboardList, roles: ALL },
      ],
    },
    {
      id: 'admin',
      label: 'Admin / System',
      items: [
        { name: 'Reports',        path: '/reports',        icon: BarChart3, roles: ALL },
        { name: 'Diesel Prices',  path: '/admin/diesel-prices', icon: Fuel, roles: ALL },
        { name: 'Data Ingestion', path: '/data-ingestion', icon: Database,  roles: ALL },
        { name: 'Alarms',         path: '/alarms',         icon: Bell,      roles: ALL, badge: 'alarms' },
        { name: 'Audit Logs',     path: '/audit-logs',     icon: FileText,  roles: ALL },
        { name: 'Users',          path: '/users',          icon: Settings,  roles: ALL },
      ],
    },
  ],
});

// Map alias → canonical path so the active highlight resolves either way.
const ALIAS_TO_CANONICAL = {
  '/sales/inbox': '/sales-inbox',
  '/sales/prospects': '/prospects',
  '/sales/verify-queue': '/verification-queue',
  '/sales/proposals': '/proposals',
  '/sales/pricing-catalog': '/pricing',
};


export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Collapse state per section, persisted
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { console.error('Sidebar collapse state parse failed', e); return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch (e) { console.error('Sidebar collapse state persist failed', e); }
  }, [collapsed]);

  // Badge counts (Sales Inbox unread + Verify Queue pending + Active alarms).
  // Best-effort — hidden if endpoints fail or return 0.
  const [badges, setBadges] = useState({ inbox: 0, verify: 0, alarms: 0 });
  const [alarmSeverity, setAlarmSeverity] = useState('info'); // critical|warning|info
  useEffect(() => {
    let cancelled = false;
    const auth = { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || localStorage.getItem('access_token')) } };
    const fetchAll = async () => {
      try {
        const r = await axios.get(`${API}/sales-inbox?limit=200`, auth);
        const unread = (r.data?.rows || []).filter(m =>
          ['received', 'processed', 'needs_review'].includes(m.status)
        ).length;
        if (!cancelled) setBadges(b => ({ ...b, inbox: unread }));
      } catch (e) { console.error('Sidebar inbox badge fetch failed', e); }
      try {
        const r = await axios.get(`${API}/verification-queue?status=PENDING&limit=1`, auth);
        const c = r.data?.total ?? (r.data?.rows ? r.data.rows.length : 0);
        if (!cancelled) setBadges(b => ({ ...b, verify: c || 0 }));
      } catch (e) { console.error('Sidebar verify badge fetch failed', e); }
      try {
        const r = await axios.get(`${API}/alarms/counts`, auth);
        const c = r.data || {};
        if (!cancelled) {
          setBadges(b => ({ ...b, alarms: c.total || 0 }));
          setAlarmSeverity(c.critical > 0 ? 'critical' : c.warning > 0 ? 'warning' : 'info');
        }
      } catch (e) { console.error('Sidebar alarms badge fetch failed', e); }
    };
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const ALL = ['super_admin', 'admin', 'management', 'engineering', 'sales', 'client'];
  const config = NAV_CONFIG(ALL);

  // True if the given item (or its alias target) matches the current route
  const isItemActive = (item) => {
    const candidates = [item.path];
    if (item.aliasFor) candidates.push(item.aliasFor);
    // Also reverse: if user is on an alias path, light up the canonical too
    Object.entries(ALIAS_TO_CANONICAL).forEach(([alias, canon]) => {
      if (item.path === canon) candidates.push(alias);
      if (item.path === alias) candidates.push(canon);
    });
    return candidates.some(p =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    );
  };

  const renderItem = (item) => {
    if (!item.roles.includes(user?.role)) return null;
    const Icon = item.icon;
    const active = isItemActive(item);
    const badgeKey = item.badge;
    const badgeVal = badgeKey ? badges[badgeKey] : 0;
    return (
      <li key={item.path}>
        <Link
          to={item.path}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          className={`flex items-center space-x-3 px-3 py-2 rounded-sm text-sm transition-all
            ${active
              ? 'bg-slate-700/80 text-white font-medium border-l-2 border-yellow-400 pl-[10px]'
              : 'text-slate-400 hover:bg-slate-700/40 hover:text-slate-200'
            }`}
        >
          <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={active ? 2 : 1.5} />
          <span className="flex-1 truncate">{item.name}</span>
          <span className="w-7 flex justify-end" aria-hidden>
            {badgeVal > 0 && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full
                  ${badgeKey === 'alarms'
                    ? alarmSeverity === 'critical'
                      ? 'bg-rose-500/30 text-rose-200 ring-1 ring-rose-400/50'
                      : alarmSeverity === 'warning'
                        ? 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-400/50'
                        : 'bg-sky-500/20 text-sky-200'
                    : active
                      ? 'bg-yellow-400 text-slate-900'
                      : 'bg-indigo-500/20 text-indigo-300'}`}
                data-testid={`nav-badge-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {badgeVal > 99 ? '99+' : badgeVal}
              </span>
            )}
          </span>
        </Link>
      </li>
    );
  };

  const toggleSection = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  return (
    <>
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-800 text-white rounded-sm shadow-lg"
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setMobileOpen(false)} />
      )}

      <div className={`
        w-64 bg-slate-800 h-screen flex flex-col fixed left-0 top-0 z-40
        transition-transform duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-5 border-b border-slate-700/50">
          <Link to="/dashboard" className="flex items-center" onClick={() => setMobileOpen(false)}>
            <img
              src="https://customer-assets.emergentagent.com/job_battery-central/artifacts/cp6kecw1_voltide_logo_plain.svg"
              alt="Voltide Logo"
              className="h-8 w-auto brightness-0 invert"
            />
          </Link>
          <span className="block text-[10px] text-slate-500 tracking-widest uppercase mt-1.5 font-heading">Command Center</span>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="sidebar-nav">
          {/* Top-level (no section) */}
          <ul className="space-y-0.5">{config.top.map(renderItem)}</ul>

          {/* Sections */}
          {config.sections.map(section => {
            const visibleItems = section.items.filter(i => i.roles.includes(user?.role));
            if (visibleItems.length === 0) return null;
            const isCollapsed = !!collapsed[section.id];
            return (
              <div key={section.id} className="pt-5">
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-3 pb-1.5 group"
                  data-testid={`section-toggle-${section.id}`}
                  aria-expanded={!isCollapsed}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 group-hover:text-slate-300">
                    {section.label}
                  </span>
                  <ChevronDown
                    className={`w-3 h-3 text-slate-600 group-hover:text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>
                {!isCollapsed && (
                  <ul className="space-y-0.5">{visibleItems.map(renderItem)}</ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-slate-700/50">
          <div className="flex items-center space-x-3 px-3 mb-3">
            <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center ring-2 ring-slate-500">
              <span className="text-slate-200 font-semibold text-xs">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center space-x-2 px-3 py-2 rounded-sm text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};
